import React from 'react';
import { useSelector } from 'react-redux';
import { ExternalScripts } from 'mm-ui-components';
import {
  getExternalScripts,
  getCanonicalUrl,
  getCmpId,
  getAuthorIds,
  getProperty,
  getPageType,
  getEdition,
  getTemplateName,
  getEditionEndpoint,
  getPropertyEndpoint,
  getExperiments,
  getIsFannation,
  getAuthors,
  getMainCategory,
} from '../../store/config/config.selectors';
import { ExternalScripts as ExternalScriptsType } from '../../store/config/config.reducer';
import { getCWVScript } from '../../utilities/getCWVScript';
import { updateBCHemCookie } from '../../utilities/getUpdateBCCookie';
import { getCommercialTags } from '../../store/template/postPage/postPageAmsterdam/postPageAmsterdam.selectors';
import { getBlueConicScript } from '../../utilities/getBlueConicScript';
import { getSailthruScripts } from '../../utilities/getSailthruScripts';
import { hasGoogleOAuth } from '../../utilities/hasGoogleOauth';
import { getRetentionScript } from '../../utilities/getRetentionScript';

export const CMP_EXCLUDED_PROPERTIES = [''];

const ONE_TRUST_DEFAULT_ID = '01948883-cdc5-73d6-a267-dd1f0a86d169';

function getOneTrustScripts(cmpId: string | null): ExternalScriptsType {
  const oneTrustID = cmpId || ONE_TRUST_DEFAULT_ID;

  const scripts: ExternalScriptsType = [];

  scripts.push({
    type: 'URL' as const,
    source: 'https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js',
    name: 'OTURL',
    attributes: {
      'data-domain-script': oneTrustID,
    },
    loadingType: 'blocking' as const,
  });

  scripts.push({
    type: 'URL' as const,
    source: 'https://cdn.cookielaw.org/opt-out/otCCPAiab.js',
    name: 'OTIAB',
    attributes: {
      'ccpa-opt-out-ids': 'C0004' as const,
      'ccpa-opt-out-geo': 'ca' as const,
      'ccpa-opt-out-lspa': 'true' as const,
    },
    loadingType: 'blocking' as const,
  });

  scripts.push({
    type: 'code' as const,
    source: `
      function OptanonWrapper() {
        const geoData = OneTrust.getGeolocationData?.();
        const isUS = (geoData?.country === "US");
        const groups = window.OnetrustActiveGroups || '';
        const consentGiven = groups.includes(',C0004,');

        const shouldLoadAutoBlockScript = !isUS || (isUS && !consentGiven);

        if (shouldLoadAutoBlockScript) {
          loadAutoBlockScript();
        } else {
          OneTrust.OnConsentChanged(function(eventData) {
            loadAutoBlockScript();
            location.reload();
          });
        }

        function loadAutoBlockScript() {
          if (!document.getElementById("OTAutoBlock")) {
            var script = document.createElement("script");
            script.type = "text/javascript";
            script.src = "https://cdn-ukwest.onetrust.com/consent/${oneTrustID}/OtAutoBlock.js";
            script.id = "OTAutoBlock";
            document.head.appendChild(script);
          }
        }
      }
    `,
    name: 'OTInit',
    attributes: null,
    loadingType: 'blocking' as const,
  });

  return scripts;
}

export const HeadScripts: React.FunctionComponent = () => {
  const scripts = useSelector(getExternalScripts);
  const isRegistrationWall = useSelector(getCommercialTags)?.includes('registration-wall') || false;
  const canonicalUrl = useSelector(getCanonicalUrl);
  const edition = useSelector(getEdition);
  const editionEndpoint = useSelector(getEditionEndpoint);
  const cmpId = useSelector(getCmpId);
  const property = useSelector(getProperty);
  const authorIds = useSelector(getAuthorIds);
  const pageType = useSelector(getPageType);
  const templateName = useSelector(getTemplateName);
  const isPropertyExcluded = CMP_EXCLUDED_PROPERTIES.includes(property);
  const propertyEndpoint = useSelector(getPropertyEndpoint);
  const experiments = useSelector(getExperiments);
  const urlEndpoint = editionEndpoint || propertyEndpoint || '';
  const hasGoogleAuth = hasGoogleOAuth(edition, propertyEndpoint, experiments);
  const hasMicrosoftOAuthExperiment = experiments?.some((experiment: string) => experiment.includes('microsoft-oauth') && experiment.includes('modal'));
  const authors = useSelector(getAuthors)?.map(author => author.name)?.join(',') || '';
  const mainCategory = useSelector(getMainCategory)?.displayName || '';
  const isFannation = useSelector(getIsFannation);
  // SI core sites don't contain a slash ex. www.si.com, non SI core sites do ex. www.si.com/es-us
  const isEdgeCase = !isFannation && urlEndpoint.includes('.si.com/');
  const ONSIDomain = urlEndpoint.includes('.si.com')
    ? `${edition.replace(/_/g, '-')}${isEdgeCase ? '' : '.on.si'}.com`
    : '';
  const domain = isFannation || isEdgeCase ? ONSIDomain : urlEndpoint;

  if (!isPropertyExcluded) {
    scripts.unshift(...getOneTrustScripts(cmpId));
  }

  /** Retention Script */
  if (canonicalUrl) {
    scripts.push(...getRetentionScript(canonicalUrl));
  }

  /** Chartbeat */
  const chartbeatData = `
    window._sf_async_config = {
      uid: 67224,
      domain: "${domain}",
      useCanonical: true,
      useCanonicalDomain: true,
      flickerControl: false,
      sections: "${mainCategory || pageType || ''}",
      authors: "${authors}",
    };
  `;

  scripts.push({
    type: 'code',
    source: chartbeatData,
    name: 'chartbeatData',
    attributes: null,
    loadingType: 'async',
  });

  scripts.push({
    type: 'URL',
    source: 'https://static.chartbeat.com/js/chartbeat_mab.js',
    attributes: null,
    loadingType: 'async',
    name: 'chartbeatHeadline',
  });

  scripts.push({
    type: 'URL',
    source: 'https://static.chartbeat.com/js/chartbeat.js',
    attributes: null,
    loadingType: 'async',
    name: 'chartbeat',
  });

  /** Google OAuth */
  if (hasGoogleAuth) {
    scripts.push({
      type: 'URL',
      source: 'https://accounts.google.com/gsi/client',
      name: 'googleOAuth',
      attributes: null,
      loadingType: 'async',
    });
  }

  /** Microsoft OAuth */
  if (hasMicrosoftOAuthExperiment) {
    scripts.push({
      type: 'URL',
      source: 'https://edge-auth.microsoft.com/js/ms_auth_client.min.js',
      name: 'microsoftOAuth',
      attributes: null,
      loadingType: 'async',
    });
  }

  /** BlueConic */
  scripts.push({
    type: 'code',
    source: `
    window.mmBCPageData = {
      property: '${property}',
      authors: ${JSON.stringify(authorIds)},
      pageType: '${pageType}',
      templateName: '${templateName}',
      isRegistrationWall: '${isRegistrationWall}',
    };`,
    name: 'mmBCPageData',
    attributes: null,
    loadingType: 'blocking',
  });

  scripts.push(...getBlueConicScript(canonicalUrl, templateName, edition, urlEndpoint));

  /** CoreWebVitals */
  if (canonicalUrl) {
    const cwvScript = getCWVScript(canonicalUrl);
    if (cwvScript) {
      scripts.push(cwvScript);
    }
  }

  /** Sailthru */
  if (pageType === 'PostPage') {
    scripts.push(...getSailthruScripts(urlEndpoint, templateName));
  }

  /** Recirculation Widget */
  scripts.push({
    type: 'URL',
    source: 'https://assets.minutemediacdn.com/recirculation-widget/prod/mm-recirculation-injector.js',
    name: 'RecirculationWidget',
    attributes: null,
    loadingType: 'async',
  });

  scripts.push(updateBCHemCookie());

  return <ExternalScripts scripts={scripts} />;
};
